import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ShieldCheck, Radio, Zap, Headphones, ArrowRight, Trophy, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { money, dateOnly } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { MatchCard, GameCard } from "@/components/SportsCards";
import { Skeleton } from "@/components/ui/skeleton";

const FEATURES = [
  [ShieldCheck, "Secure Account", "Hashed passwords, session controls and login protection."],
  [Radio, "Live Sports Coverage", "Live event layouts ready for an authorised data provider."],
  [Zap, "Fast Transaction Processing", "Every deposit and withdrawal has a clear, tracked status."],
  [Headphones, "Customer Support", "Raise and track support tickets from your account."],
];

const SPORTS = [
  ["football", "Football", "Premier League · La Liga · UCL", "/brand/football.jpg"],
  ["cricket", "Cricket", "T20 World Cup · Indian T20", "/brand/cricket.jpg"],
  ["basketball", "Basketball", "Pro League · EuroLeague", "/brand/basketball.jpg"],
  ["tennis", "Tennis", "Grand Slams · ATP Masters", "/brand/tennis.jpg"],
  ["esports", "Esports", "CS Major · Dota Pro Circuit", "/brand/esports.jpg"],
];

const Section = ({ eyebrow, title, link, linkLabel = "View all", children, testid }) => (
  <section data-testid={testid} className="mx-auto max-w-7xl px-5 py-14">
    <div className="mb-8 flex items-end justify-between gap-4">
      <div>
        {eyebrow && <p className="mb-2 text-xs font-bold uppercase tracking-[0.25em] text-cr-gold">{eyebrow}</p>}
        <h2 className="text-2xl font-extrabold uppercase sm:text-3xl">{title}</h2>
      </div>
      {link && <Link to={link} data-testid={`${testid}-view-all`} className="flex shrink-0 items-center gap-1 text-sm font-semibold text-cr-gold hover:underline">{linkLabel}<ArrowRight className="h-4 w-4" /></Link>}
    </div>
    {children}
  </section>
);

const BonusCard = ({ bonus }) => (
  <div data-testid="hero-bonus-card" className="card-gold relative w-full max-w-sm overflow-hidden p-7 text-center backdrop-blur-xl">
    <div className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-cr-gold/60"><Gift className="h-8 w-8 text-cr-gold" /></div>
    <p className="mt-4 font-display text-lg font-semibold uppercase tracking-wide">{bonus.label}</p>
    <p className="mt-1 font-display text-3xl font-extrabold"><span className="gold-text">{bonus.percent}%</span> <span className="text-xl">UP TO</span></p>
    <p data-testid="hero-bonus-cap" className="font-display text-5xl font-black gold-text">{money(bonus.cap, { whole: true })}</p>
    <p className="mt-1 font-display text-sm font-semibold uppercase">{bonus.headline}</p>
    <div className="my-5 h-px bg-cr-line" />
    <p className="text-xs text-cr-ink/75"><b className="text-cr-ink">Eligibility:</b> {bonus.eligibility}</p>
    <p className="mt-2 text-[11px] leading-relaxed text-cr-muted">{bonus.terms}</p>
    <Link to="/promotions" data-testid="hero-bonus-terms-link" className="mt-3 inline-block text-xs font-semibold text-cr-gold underline">Full terms</Link>
  </div>
);

const Hero = () => {
  const { config, user } = useAuth();
  const l = config?.landing;
  return (
    <section data-testid="hero-section" className="grain relative overflow-hidden">
      <img src="/brand/hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover object-right opacity-90" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#080909] via-[#080909]/85 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#080909] to-transparent" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 lg:grid-cols-[1.3fr_1fr] lg:py-28">
        <div className="fade-up">
          <h1 data-testid="hero-title" className="text-5xl font-black uppercase leading-[0.95] sm:text-6xl lg:text-7xl">
            {(l?.hero_title || "PLAY. PREDICT. WIN BIG.").replace("WIN BIG.", "")}<span className="gold-text">WIN BIG.</span>
          </h1>
          <p data-testid="hero-subtitle" className="mt-6 max-w-xl text-base text-cr-ink/80 sm:text-lg">{l?.hero_subtitle}</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to={user ? "/account" : "/register"} data-testid="hero-start-playing-btn" className="btn-gold px-8 py-3.5 text-base">Start Playing<ArrowRight className="h-5 w-5" /></Link>
            <Link to="/sports" data-testid="hero-explore-sports-btn" className="btn-outline px-8 py-3.5 text-base">Explore Sports</Link>
          </div>
          {!user && <p className="mt-6 text-sm text-cr-muted">Already a member? <Link to="/login" data-testid="hero-login-link" className="font-semibold text-cr-gold">Sign in</Link> · New here? <Link to="/register" data-testid="hero-register-link" className="font-semibold text-cr-gold">Create an account</Link></p>}
        </div>
        <div className="flex justify-center lg:justify-end fade-up" style={{ animationDelay: ".15s" }}>{l?.bonus && <BonusCard bonus={l.bonus} />}</div>
      </div>
    </section>
  );
};

const useList = (url) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(url).then((r) => setData(r.data)).catch(() => setData([])); }, [url]);
  return data;
};

const Grid = ({ items, render, cols = "sm:grid-cols-2 lg:grid-cols-4", count = 4 }) => (
  <div className={`grid grid-cols-1 gap-5 ${cols}`}>
    {items === null ? Array.from({ length: count }).map((_, i) => <Skeleton key={i} className="h-56 rounded-xl bg-cr-elevated" />) : items.map(render)}
  </div>
);

export default function Landing() {
  const live = useList("/cricket/matches?status=live");
  const games = useList("/games");
  const promos = useList("/promotions");
  const g = (fn) => (games === null ? null : games.filter(fn).slice(0, 4));
  return (
    <main data-testid="landing-page">
      <Hero />
      <section className="mx-auto -mt-6 max-w-7xl px-5">
        <div data-testid="feature-cards" className="grid grid-cols-1 gap-4 rounded-2xl border border-cr-line bg-cr-surface/90 p-5 backdrop-blur sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map(([Icon, t, d]) => (
            <div key={t} data-testid={`feature-${t.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-start gap-4 p-2">
              <Icon className="h-9 w-9 shrink-0 text-cr-gold" />
              <div><p className="font-display font-bold uppercase">{t}</p><p className="mt-1 text-sm text-cr-muted">{d}</p></div>
            </div>
          ))}
        </div>
      </section>

      <Section eyebrow="Sportsbook" title="Top Sports" link="/sports" testid="sports-section">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {SPORTS.map(([k, name, comp, img]) => (
            <Link key={k} to={`/sports?sport=${k}`} data-testid={`sport-card-${k}`} className="group relative aspect-[3/4] overflow-hidden rounded-xl border border-cr-line">
              <img src={img} alt={name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <p className="font-display text-xl font-extrabold uppercase">{name}</p>
                <p className="mt-1 text-xs text-cr-ink/70">{comp}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase text-cr-gold">View events<ArrowRight className="h-3 w-3" /></span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section eyebrow="In play" title="Live Cricket" link="/sports/live" testid="live-section">
        {live?.length === 0 && <p className="-mt-4 mb-6 text-sm text-cr-muted">No live matches right now. <Link to="/sports/upcoming" className="text-cr-gold">See upcoming matches →</Link></p>}
        <Grid items={live?.slice(0, 4) ?? null} render={(m) => <MatchCard key={m.id} m={m} />} />
      </Section>

      <Section eyebrow="Casino" title="Casino Games" link="/casino" testid="casino-section">
        <Grid items={g((x) => x.section === "casino")} render={(x) => <GameCard key={x.id} game={x} />} />
      </Section>
      <Section eyebrow="Live tables" title="Live Casino" link="/live-casino" testid="live-casino-section">
        <Grid items={g((x) => x.section === "live")} render={(x) => <GameCard key={x.id} game={x} />} />
      </Section>
      <div className="mx-auto grid max-w-7xl gap-0 lg:grid-cols-2">
        <Section title="Popular Games" testid="popular-section">
          <Grid cols="grid-cols-2" items={g((x) => x.tags.includes("popular"))} render={(x) => <GameCard key={x.id} game={x} />} />
        </Section>
        <Section title="Recently Added" testid="recent-section">
          <Grid cols="grid-cols-2" items={g((x) => x.tags.includes("new"))} render={(x) => <GameCard key={x.id} game={x} />} />
        </Section>
      </div>

      <Section eyebrow="Offers" title="Promotions" link="/promotions" testid="promotions-section">
        <Grid cols="md:grid-cols-3" count={3} items={promos} render={(p) => (
          <div key={p.id} data-testid={`promo-card-${p.id}`} className="card-cr overflow-hidden">
            <div className="relative h-36"><img src={p.image} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-cr-surface to-transparent" /></div>
            <div className="p-5">
              <p className="font-display text-lg font-bold">{p.title}</p>
              <p className="mt-1 text-sm text-cr-ink/75">{p.subtitle}</p>
              <p className="mt-3 text-xs text-cr-muted">Eligibility: {p.eligibility} · Expires {dateOnly(p.expires_at)}</p>
              <Link to={p.cta_link} data-testid={`promo-cta-${p.id}`} className="btn-outline mt-4 px-4 py-2 text-xs">{p.cta_label}</Link>
            </div>
          </div>
        )} />
      </Section>

      <section className="mx-auto max-w-7xl px-5 pb-16">
        <div className="relative overflow-hidden rounded-2xl border border-cr-gold/40 bg-gradient-to-r from-cr-gold/15 via-cr-surface to-cr-surface p-8 sm:p-12">
          <Crown className="absolute -right-6 -top-6 h-48 w-48 text-cr-gold/10" />
          <Trophy className="h-10 w-10 text-cr-gold" />
          <h3 className="mt-4 text-3xl font-black uppercase">Join the <span className="gold-text">VIP Club</span></h3>
          <p className="mt-2 max-w-lg text-cr-ink/75">Tiered membership with personalised support and exclusive offers.</p>
          <Link to="/vip" data-testid="vip-cta-btn" className="btn-gold mt-6">Explore VIP<ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </main>
  );
}
